/**
 * @fileoverview SideBar Component - Main navigation sidebar for the Clockwise application
 *
 * Provides a comprehensive navigation sidebar with the following features:
 * - Permission-based navigation items that dynamically show/hide based on user roles
 * - Responsive design with mobile hamburger menu and desktop collapsible sidebar
 * - Drag-and-drop reordering of navigation items (desktop only) with persistent storage
 * - User information display with avatar and role
 * - Recent items integration for quick access to previously viewed content
 * - Loading states for navigation transitions
 * - User logout functionality with proper cleanup
 *
 * The component integrates deeply with the authentication system to:
 * - Display appropriate navigation items based on user permissions
 * - Show user profile information and role
 * - Handle secure logout with cookie cleanup
 *
 * Navigation customization features:
 * - Drag-and-drop reordering (desktop only)
 * - Per-user navigation order persistence via localStorage
 * - Edit mode toggle for customization
 * - Reset to default order functionality
 *
 * Responsive behavior:
 * - Mobile: Hamburger menu with overlay
 * - Desktop: Collapsible sidebar with tooltip support
 * - Automatic responsive breakpoint detection and handling
 */

import { useEffect, useState, memo, useCallback, useMemo, forwardRef, useImperativeHandle } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useNotify } from "../context/NotificationContext";
import "../styles/SideBar.css"
import {
    FiActivity,
    FiChevronLeft,
    FiClock,
    FiDollarSign,
    FiMenu,
    FiPieChart,
    FiSettings,
    FiUsers,
    FiLogOut,
    FiX,
    FiBook,
    FiMail,
    FiUser,
    FiCompass
} from "react-icons/fi";
import { MdAdminPanelSettings } from "react-icons/md";
import RecentItems from "./RecentItems";

/**
 * SideBar - Main navigation sidebar component for the Clockwise application
 *
 * Renders a comprehensive navigation sidebar that adapts to user permissions and device type.
 * Features include permission-based navigation, drag-and-drop reordering, responsive design,
 * user profile display, recent items integration, and seamless logout functionality.
 *
 * Key Features:
 * - Permission-based navigation items that dynamically show/hide based on user roles
 * - Responsive design with mobile hamburger menu and desktop collapsible sidebar
 * - Drag-and-drop reordering with per-user persistence via localStorage
 * - Loading states and navigation feedback
 * - Recent items integration for quick access
 *
 * State Management:
 * - Manages sidebar collapse/expand state
 * - Tracks mobile menu visibility
 * - Handles edit mode for navigation customization
 * - Maintains drag-and-drop state
 * - Stores custom navigation order per user
 * - Tracks loading states for navigation transitions
 *
 * Responsive Behavior:
 * - Mobile (≤768px): Hamburger menu with full-screen overlay
 * - Desktop (>768px): Collapsible sidebar with hover tooltips
 * - Automatic responsive state detection and handling
 *
 * Permission Integration:
 * - Checks user.full_role permissions for each navigation item
 * - Supports 'all' permission for admin users
 * - Dynamically filters navigation based on user capabilities
 *
 * Navigation Customization:
 * - Drag-and-drop reordering (desktop only)
 * - Per-user order persistence via localStorage key: nav_order_${user.id}
 * - Edit mode toggle with visual feedback
 * - Reset to default order functionality
 *
 * @returns {JSX.Element|null} The sidebar component or null if user is not authenticated
 */
const SideBar = memo(forwardRef((props, ref) => {
    const { logout, user, clearAuthCookies, DEV, FILE_APP_URL, getEffectiveRole, apiRequest } = useAuth(); // eslint-disable-line no-unused-vars
    const { success, error } = useNotify(); // eslint-disable-line no-unused-vars
    const location = useLocation();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileMenuExpanded, setMobileMenuExpanded] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [loadingPath, setLoadingPath] = useState(null);
    const [effectiveRole, setEffectiveRole] = useState(null);
    const [orientationCompleted, setOrientationCompleted] = useState(false);
    const navigate = useNavigate();

    // Expose toggleSidebar function to parent via ref
    useImperativeHandle(ref, () => ({
        toggleSidebar
    }));

    // State to trigger re-fetch when emulated role changes
    const [emulatedRoleTrigger, setEmulatedRoleTrigger] = useState(0);

    // Listen for storage events (role emulation changes)
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'emulated_role_id') {
                console.log('🎭 Emulated role changed, refreshing sidebar...');
                setEmulatedRoleTrigger(prev => prev + 1);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Fetch effective role using centralized method from AuthContext
    useEffect(() => {
        const fetchEffectiveRole = async () => {
            if (!user?.full_role) {
                setEffectiveRole(null);
                return;
            }

            const role = await getEffectiveRole();
            setEffectiveRole(role);
        };

        fetchEffectiveRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, getEffectiveRole, emulatedRoleTrigger])

    // Fetch orientation completion status
    useEffect(() => {
        const fetchOrientationStatus = async () => {
            if (!user?.id) return;

            try {
                const response = await apiRequest('/user/orientation-progress');
                if (response.ok) {
                    const data = await response.json();
                    setOrientationCompleted(data.completed || false);
                }
            } catch (err) {
                // Silently fail - orientation link will remain visible
                console.error('Failed to fetch orientation status:', err);
            }
        };

        fetchOrientationStatus();
    }, [user, apiRequest]);

    // Clear loading state when location changes
    useEffect(() => {
        setLoadingPath(null);
    }, [location.pathname]);

    // Handle navigation with loading state
    const handleNavigation = useCallback((path, event) => {
        if (path !== location.pathname) {
            setLoadingPath(path);
        }

        if (isMobile) {
            setMobileMenuExpanded(false);
        }
    }, [location.pathname, isMobile]);

    const handleLogout = useCallback(async () => {
        try {
            await logout(); // Wait for logout to complete
            clearAuthCookies()
            navigate("/login");
        } catch (err) {
            console.error('Logout failed:', err);
            clearAuthCookies()
            navigate("/login"); // Still navigate even if logout fails
        }
    }, [logout, clearAuthCookies, navigate]);

    useEffect(() => {
        const root = document.querySelector(':root');
        if (!user) {
            root.style.setProperty('--sidebar-width', '0px')
        } else {
            root.style.setProperty('--sidebar-width', sidebarCollapsed ? '70px' : '250px')
        }
    }, [sidebarCollapsed, user])

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (!mobile) {
                setMobileMenuExpanded(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleSidebar = useCallback(() => {
        // Check current window width to ensure accurate mobile detection
        const currentIsMobile = window.innerWidth <= 768;
        if (currentIsMobile) {
            setMobileMenuExpanded(!mobileMenuExpanded);
        } else {
            setSidebarCollapsed(!sidebarCollapsed);
        }
    }, [mobileMenuExpanded, sidebarCollapsed]);

    // Helper function to check if user has permission
    const hasPermission = useCallback((permission) => {
        if (effectiveRole) {
            return (effectiveRole['all'] || effectiveRole[permission])
        }
        return false;
    }, [effectiveRole]);

    // Get navigation items based on user permissions
    const navItems = useMemo(() => {
        if (!user) return [];

        const items = [
            // Dashboard - available to all authenticated users
            { id: 'dashboard', to: "/", icon: FiActivity, text: "Dashboard", tooltip: "Dashboard" },

            // Time Tracking - available to users who can view time entries
            ...(hasPermission('can_view_time_entries') ? [
                { id: 'time_tracking', to: "/profile", icon: FiClock, text: "Time Tracking", tooltip: "Time Tracking" }
            ] : []),
        ];

        // Client Management section
        if (hasPermission('can_view_clients')) {
            items.push({ id: 'clients', to: "/clients/manage", icon: FiUsers, text: "Clients", tooltip: "Clients" });
        }

        // Financial section
        if (hasPermission('can_view_billing')) {
            items.push({ id: 'billing', to: "/billing", icon: FiDollarSign, text: "Billing", tooltip: "Billing" });
        }

        if (hasPermission('can_view_ar_ledger')) {
            items.push({ id: 'ar_ledger', to: "/ledger", icon: FiBook, text: "AR Ledger", tooltip: "Accounts Receivable" });
        }

        if (hasPermission('can_view_reports_page')) {
            items.push({ id: 'reports', to: "/reports", icon: FiPieChart, text: "Reports", tooltip: "Reports" });
        }

        // Admin section - only show if user has admin page access
        if (hasPermission('can_view_admin_page')) {
            items.push({ id: 'admin', to: "/admin", icon: MdAdminPanelSettings, text: "Admin", tooltip: "Admin Panel" });
        }

        // Support - always available, placed after admin section
        items.push(
            { id: 'support', to: "/support-tickets", icon: FiMail, text: "Support", tooltip: "My Support Tickets" }
        );

        // Orientation - only show if not completed
        if (!orientationCompleted) {
            items.push(
                { id: 'orientation', to: "/orientation", icon: FiCompass, text: "Orientation", tooltip: "App Orientation & Tutorial" }
            );
        }

        return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, hasPermission, orientationCompleted]);

    // Helper function to get user role display name
    const getUserRoleDisplay = useMemo(() => {
        if (!user) return '';
        if (user.role && user.role.name) return user.role.name;
        if (user.role_name) return user.role_name;
        return 'User';
    }, [user]);

    return user ? (
        <>
            {/* Mobile Menu Button - Removed, now in TopBar */}

            {/* Mobile Backdrop */}
            {mobileMenuExpanded && (
                <div
                    className={`sidebar-backdrop ${mobileMenuExpanded ? 'active' : ''}`}
                    onClick={() => setMobileMenuExpanded(false)}
                />
            )}

            <aside className={`dashboard-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuExpanded ? 'mobile-expanded' : ''} element-to-hide`}>
                <div className="sidebar-header">
                    <div className="sidebar-logo-section">
                        <Link to="/" className="sidebar-logo-link">
                            <img
                                src="/clockwise.png"
                                alt="Clockwise"
                                className="sidebar-logo-icon"
                            />
                            <span className="sidebar-logo-text">Clockwise</span>
                        </Link>
                    </div>
                    {!sidebarCollapsed ? (
                        <button
                            className="collapse-btn"
                            onClick={toggleSidebar}
                            aria-label={isMobile ? "Close Menu" : "Collapse Sidebar"}
                        >
                            {isMobile ? <FiX /> : <FiChevronLeft />}
                        </button>
                    ) : (
                        <button
                            className="collapse-btn"
                            onClick={toggleSidebar}
                            aria-label="Expand Sidebar"
                            title={`Expand Sidebar (${getUserRoleDisplay})`}
                        >
                            <FiMenu />
                        </button>
                    )}
                </div>

                <nav className="sidebar-nav">
                    {/* Dynamic Navigation Items */}
                    <div className="nav-section">
                        <ul className="nav-list">
                            {navItems.map((item) => {
                                const IconComponent = item.icon;
                                return (
                                    <li key={item.id} className="nav-item">
                                        <NavLink
                                            to={item.to}
                                            className={({ isActive }) => `${isActive ? "active" : ""} ${loadingPath === item.to ? "loading" : ""}`}
                                            data-tooltip={item.tooltip}
                                            onClick={(e) => handleNavigation(item.to, e)}
                                            end
                                        >
                                            <div className="nav-link-content">
                                                <IconComponent />
                                                <span className="nav-text">{item.text}</span>
                                                {loadingPath === item.to && (
                                                    <div className="nav-loading-indicator">
                                                        <div className="nav-spinner"></div>
                                                    </div>
                                                )}
                                            </div>
                                        </NavLink>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    {/* Recent Items - Only show when not collapsed and not mobile */}
                    {!sidebarCollapsed && !isMobile && (
                        <div className="nav-section recent-section">
                            <RecentItems limit={5} />
                        </div>
                    )}

                    {/* Bottom section - Settings and Account */}
                    <div className="nav-section bottom-section">
                        <ul>
                            <li className="nav-item">
                                <NavLink
                                    to="/account"
                                    className={({ isActive }) => isActive ? "active" : ""}
                                    data-tooltip="My Account"
                                    onClick={() => { if (isMobile) setMobileMenuExpanded(false); }}
                                >
                                    <FiUser />
                                    <span className="nav-text">My Account</span>
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    to="/settings"
                                    className={({ isActive }) => isActive ? "active" : ""}
                                    data-tooltip="Settings"
                                    onClick={() => { if (isMobile) setMobileMenuExpanded(false); }}
                                >
                                    <FiSettings />
                                    <span className="nav-text">Settings</span>
                                </NavLink>
                            </li>
                            <li className="nav-item logout-item">
                                <Link
                                    to="/login"
                                    onClick={(e) => {
                                        handleLogout();
                                        if (isMobile) setMobileMenuExpanded(false);
                                    }}
                                    data-tooltip="Logout"
                                >
                                    <FiLogOut />
                                    <span className="nav-text">Logout</span>
                                </Link>
                            </li>
                        </ul>
                    </div>
                </nav>
            </aside>
        </>
    ) : null;
}));

SideBar.displayName = 'SideBar';

export default SideBar;